<template>
  <div class="wrapper">
    <Preloader/>
    <Navigation :data="data" v-if="ready"/>
    <main class="main">
      <Breadcrumbs :data="data" v-if="ready"/>
      <Component :is="instance" :data="data" v-if="ready"/>
      <Footer :data="data" v-if="ready"/>
      <Copyright :data="data" v-if="ready"/>
      <Cookies :data="data" v-if="ready"/>
    </main>
    <ScrollToTop :data="data" v-if="ready"/>
  </div>
</template>

<script>
/* Mixins */
import {Default} from '@mixins/Default';

/* Components */
import Preloader from '@components/App/Partials/Preloader';
import Navigation from '@components/App/Partials/Navigation';
import Breadcrumbs from '@components/App/Partials/Breadcrumbs';
import Footer from '@components/App/Partials/Footer';
import Copyright from '@components/App/Partials/Copyright';
import Cookies from '@components/App/Partials/Cookies';
import ScrollToTop from '@components/App/Partials/ScrollToTop';

/* Logic */
export default {
  name: 'Index',
  mixins: [Default],
  components: {
    Preloader,
    Navigation,
    Breadcrumbs,
    Footer,
    Copyright,
    Cookies,
    ScrollToTop,
  },
  data: function () {
    return {
      data: {
        navigation: {},
        breadcrumbs: [],
        page: {
          blocks: [],
          category: {},
          content: [],
          photos: [],
          photosExtra: [],
          id: null,
          slug: '',
          translations: {},
          parent: {},
          createdAt: '',
        },
        settings: {
          currency: '',
          deliveries: [],
          locality: '',
          translatable: false,
          maintenanceMode: false,
          pageNotFound: false
        },
        shareMeta: {
          author: "",
          description: "",
          facebook_app_id: "",
          facebook_description: "",
          facebook_share_photo: "",
          facebook_title: "",
          keywords: "",
          title: "",
        },
        slugs: {},
        snippets: []
      },
      ready: true,
      meta: {
        title: '',
        author: '',
        keywords: '',
        description: '',
        facebook_title: '',
        facebook_description: '',
        locality: '',
        image: '',
        url: '',
      }
    }
  },
  methods: {
    setExpiration: function () {
        let current = new Date();
        let expiration = this.$store.state.expiration;
        if (expiration === null) {
            this.$store.dispatch('setExpiration', new Date());
        } else {
            let convertCurrent = this.Moment(current);
            let convertExpiration = this.Moment(expiration);
            let duration = this.Moment.duration(convertCurrent.diff(convertExpiration));
            let minutes = duration.asMinutes();
            if (minutes > 180) {
                this.$store.dispatch('reinitializeData');
            }
        }
    },
    fetchItems: async function () {
      this.setLoading(true);
      this.Axios.get('/api/v1/init', {
        params: {
          slug: this.$route.params.pathMatch,
          page: this.$route.query.page,
          isPreview: this.$route.query.isPreview
        }
      }).then(response => {
        this.data = {
          breadcrumbs: response.data.content.data.breadcrumbs,
          navigation: response.data.content.data.navigation,
          page: {
            blocks: response.data.content.data.page.blocks,
            category: response.data.content.data.page.category,
            content: response.data.content.data.page.content,
            photos: response.data.content.data.page.photos,
            photosExtra: response.data.content.data.page.photosExtra,
            id: response.data.content.data.page.id,
            slug: response.data.content.data.page.slug,
            translations: response.data.content.data.page.translations,
            parent: response.data.content.data.page.parent,
            createdAt: response.data.content.data.page.createdAt,
          },
          settings: {
            currency: response.data.content.data.settings.currency,
            deliveries: response.data.content.data.settings.deliveries,
            locality: response.data.content.data.settings.locality,
            translatable: response.data.content.data.settings.translatable,
            maintenanceMode: response.data.content.data.settings.maintenanceMode,
            pageNotFound: response.data.content.data.settings.pageNotFound
          },
          shareMeta: {
            author: response.data.content.data.shareMeta.author,
            description: response.data.content.data.shareMeta.description,
            facebook_app_id: response.data.content.data.shareMeta.facebook_app_id,
            facebook_description: response.data.content.data.shareMeta.facebook_description,
            facebook_share_photo: response.data.content.data.shareMeta.facebook_share_photo,
            facebook_title: response.data.content.data.shareMeta.facebook_title,
            keywords: response.data.content.data.shareMeta.keywords,
            title: response.data.content.data.shareMeta.title,
          },
          slugs: response.data.content.data.slugs,
          snippets: response.data.content.data.snippets
        };
      }).catch(() => {
        this.data = {
          breadcrumbs: [],
          navigation: {},
          page: {
            blocks: [],
            category: {},
            content: [],
            photos: [],
            photosExtra: [],
            id: null,
            slug: '',
            parent: {},
            createdAt: '',
          },
          settings: {
            currency: '',
            deliveries: [],
            locality: '',
            translatable: false,
            maintenanceMode: false,
            pageNotFound: false
          },
          shareMeta: {
            author: "",
            description: "",
            facebook_app_id: "",
            facebook_description: "",
            facebook_share_photo: "",
            facebook_title: "",
            keywords: "",
            title: "",
          },
          slugs: {},
          snippets: []
        };
      }).finally(() => {
        this.setMeta();
        this.setExpiration();
        this.setLoading(false);
      });
    }
  },
  created: async function () {
    await this.fetchItems();
    await this.setOverflow();
    await this.setWindowTop();
  },
  metaInfo: function () {
    return {
      title: this.meta.title,
      htmlAttrs: {
        lang: this.meta.locality,
        amp: false
      },
      meta: [
        {name: 'author', content: this.meta.author},
        {name: 'keywords', content: this.meta.keywords},
        {name: 'description', content: this.meta.description},
        {property: 'og:title', content: this.meta.facebook_title},
        {property: 'og:site_name', content: this.meta.author},
        {property: 'og:type', content: 'website'},
        {property: 'og:url', content: this.meta.url},
        {property: 'og:image', content: this.meta.image},
        {property: 'og:description', content: this.meta.facebook_description},
        {name: 'twitter:card', content: 'summary'},
        {name: 'twitter:site', content: this.meta.url},
        {name: 'twitter:title', content: this.meta.title},
        {name: 'twitter:description', content: this.meta.description},
        {itemprop: 'name', content: this.meta.title},
        {itemprop: 'description', content: this.meta.description},
        {itemprop: 'image', content: this.meta.image}
      ],
      link: [
        {rel: 'canonical', href: this.meta.url}
      ]
    }
  },
  computed: {
    instance() {
      if (this.data.settings.maintenanceMode) {
        this.ready = true;
        return () => import('@components/App/Errors/503/Index');
      }

      if (this.data.settings.pageNotFound === true) {
        this.ready = true;
        return () => import('@components/App/Errors/404/Index');
      }

      let category = this.data.page.category.title ? this.data.page.category.title.replace(' ', '') : null;
      let multiplicity = this.data.page.category.multiplicity ? this.data.page.category.multiplicity : null;

      this.ready = true;
      return () => import(`@components/App/Categories/${category}/${multiplicity}/Index`);
    },
    isHome() {
      return this.data.page.category.title === 'Home';
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@css/core/_variables.scss";
@import "@css/mixins/_breakpoints.scss";
@import "@css/mixins/_functions.scss";

main {
  // Mobile
  @include media-breakpoint-down(sm) {
    margin-top: 96px;
  }
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/App/Index.vue